import { Clock, MapPin, Calendar as CalendarIcon, AlertTriangle, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { useLocation } from "@/providers/LocationProvider";
import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useGooglePlacesAutocomplete } from "@/hooks/useGooglePlacesAutocomplete";
import { StepProgress } from "@/components/booking/StepProgress";
import { TimeSelector } from "@/components/booking/TimeSelector";
import { PageHeader } from "@/components/navigation/PageHeader";
import { CountryPhoneSelector } from "@/components/booking/CountryPhoneSelector";
import { getServiceZone, ServiceZone } from "@/lib/service-zones";
import { 
  NameSchema, 
  EmailSchema, 
  PasswordSchema, 
  PhoneSchema, 
  AddressSchema, 
  PostalCodeSchema,
  TimeSchema,
  DateSchema,
  NotesSchema,
  BookingInputSchema
} from "@/lib/validation/schemas";
import { sanitizeString, sanitizePhone, sanitizeEmail, sanitizeAddress } from "@/lib/validation/sanitize";
import { sanitizeError } from "@/lib/security/errorHandler";
import { checkBookingRateLimit } from "@/lib/security/rateLimit";

interface Package {
  id: string;
  name: string;
  description: string;
  duration_minutes: number;
  base_price_cents: number;
}

export default function Step1() {
  const location = useLocation();
  const navigate = useNavigate();
  const [packages, setPackages] = useState<Package[]>([]);
  const [loading, setLoading] = useState(true);

  // Get today's date in YYYY-MM-DD format for date input
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Get current time in America/Toronto timezone
  const getCurrentTimeInToronto = () => {
    const now = new Date();
    const torontoTime = new Date(now.toLocaleString('en-US', { timeZone: 'America/Toronto' }));
    let hour = torontoTime.getHours();
    let minute = Math.ceil(torontoTime.getMinutes() / 15) * 15; // arredondar para próximo 15min
    
    // Se minute for 60, avançar para a próxima hora
    if (minute === 60) {
      minute = 0;
      hour = (hour + 1) % 24;
    }
    
    const period: "am" | "pm" = hour >= 12 ? "pm" : "am";
    
    if (hour === 0) hour = 12;
    else if (hour > 12) hour = hour - 12;
    
    return { hour, minute, period };
  };

  // Check if date is weekend
  const isWeekendDate = (dateStr: string) => {
    if (!dateStr) return false;
    const date = new Date(dateStr + 'T12:00:00');
    const dayOfWeek = date.getDay();
    return dayOfWeek === 0 || dayOfWeek === 6; // 0 = Sunday, 6 = Saturday
  };

  // Find the first valid time for a given date
  const getFirstValidTime = (dateStr: string) => {
    if (!dateStr || isWeekendDate(dateStr)) {
      return getCurrentTimeInToronto();
    }

    const today = getTodayDate();
    const parseDate = (dateStr: string) => {
      const [year, month, day] = dateStr.split('-').map(Number);
      return new Date(Date.UTC(year, month - 1, day));
    };

    const selected = parseDate(dateStr);
    const current = parseDate(today);
    const diffDays = Math.round((selected.getTime() - current.getTime()) / (1000 * 60 * 60 * 24));

    // If it's tomorrow (next day booking), only PM is available, starting at 12:00 PM
    if (diffDays === 1) {
      return { hour: 12, minute: 0, period: "pm" as const };
    }

    // If it's more than 1 day in the future, only PM is available, starting at 12:00 PM
    if (diffDays > 1) {
      return { hour: 12, minute: 0, period: "pm" as const };
    }

    // If it's today, find the first valid time (current time + 1 hour buffer, rounded up)
    const now = new Date();
    const torontoTime = new Date(now.toLocaleString('en-US', { timeZone: 'America/Toronto' }));
    let hour24 = torontoTime.getHours();
    let minute = torontoTime.getMinutes();

    // Add 1 hour buffer
    minute += 60;
    if (minute >= 60) {
      hour24 += Math.floor(minute / 60);
      minute = minute % 60;
    }

    // Round up to next 15-minute interval
    minute = Math.ceil(minute / 15) * 15;
    if (minute >= 60) {
      minute = 0;
      hour24 += 1;
    }

    // If we're past 11:59 PM, we can't book for today
    if (hour24 >= 24) {
      return { hour: 12, minute: 0, period: "pm" as const };
    }

    // Convert to 12-hour format
    const period: "am" | "pm" = hour24 >= 12 ? "pm" : "am";
    let hour = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;

    return { hour, minute, period };
  };

  // Initialize state from localStorage if available
  const getInitialState = () => {
    try {
      const savedData = localStorage.getItem('bookingFormData');
      if (savedData) {
        const parsed = JSON.parse(savedData);
        if (parsed.formData && (
          parsed.formData.fullName || 
          parsed.formData.phone || 
          parsed.formData.addressLine1 ||
          parsed.selectedPackageId
        )) {
          // Ensure new fields (email, password, confirmPassword) exist with default values
          // This handles cases where old data doesn't have these fields
          return {
            formData: {
              ...parsed.formData,
              email: parsed.formData.email || "",
              password: parsed.formData.password || "",
              confirmPassword: parsed.formData.confirmPassword || "",
              phonePrefix: parsed.formData.phonePrefix || "+1",
              phoneCountryCode: parsed.formData.phoneCountryCode || undefined, // Código do país (ex: "US" ou "CA")
              city: parsed.formData.city || "Toronto",
              date: parsed.formData.date || getTodayDate(),
              time: parsed.formData.time || getCurrentTimeInToronto(),
            },
            selectedPackageId: parsed.selectedPackageId || null,
            nowFlag: parsed.nowFlag || false,
          };
        }
      }
    } catch (error) {
      console.error('Error loading initial state:', error);
    }
    
    return {
      formData: {
        fullName: "",
        phonePrefix: "+1",
        phoneCountryCode: undefined, // Será definido quando o usuário selecionar o país
        phone: "",
        email: "",
        password: "",
        confirmPassword: "",
        addressLine1: "",
        city: "Toronto",
        postalCode: "",
        addressLine2: "",
        date: getTodayDate(),
        time: getCurrentTimeInToronto(),
        notes: "",
        lat: undefined as number | undefined,
        lng: undefined as number | undefined,
        serviceZone: undefined as ServiceZone | undefined,
      },
      selectedPackageId: null,
      nowFlag: false,
    };
  };

  const initialState = getInitialState();
  
  const [formData, setFormData] = useState(initialState.formData);
  useEffect(() => {
    const today = getTodayDate();

    setFormData((prev) => {
      const updated = { 
        ...prev,
        // Ensure new fields always exist (handle cases where they might be undefined)
        email: prev.email ?? "",
        password: prev.password ?? "",
        confirmPassword: prev.confirmPassword ?? "",
      };

      if (!prev.date || prev.date < today) {
        updated.date = today;

        const now = new Date();
        const torontoTime = new Date(now.toLocaleString('en-US', { timeZone: 'America/Toronto' }));
        const hour = torontoTime.getHours();
        const minute = Math.ceil(torontoTime.getMinutes() / 15) * 15;
        const period: "am" | "pm" = hour >= 12 ? "pm" : "am";
        const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;

        updated.time = {
          hour: displayHour,
          minute: minute === 60 ? 45 : minute,
          period,
        };
      }

      return updated;
    });
  }, []);
  const [selectedPackageId, setSelectedPackageId] = useState<string | null>(initialState.selectedPackageId);
  const [nowFlag, setNowFlag] = useState(initialState.nowFlag);

  const [errors, setErrors] = useState({
    fullName: "",
    phone: "",
    email: "",
    password: "",
    confirmPassword: "",
    addressLine1: "",
    postalCode: "",
  });

  const addressInputRef = useRef<HTMLInputElement>(null);
  const cityInputRef = useRef<HTMLInputElement>(null);
  const postalCodeInputRef = useRef<HTMLInputElement>(null);
  const hasInitialized = useRef(false);
  const previousDateRef = useRef<string>(formData.date);

  // Auto-adjust time when date changes to ensure a valid time is always selected
  useEffect(() => {
    // Skip on initial mount or if date hasn't changed
    if (previousDateRef.current === formData.date || !formData.date) {
      previousDateRef.current = formData.date;
      return;
    }

    // Only adjust if date is valid and not weekend
    if (!isWeekendDate(formData.date) && !nowFlag) {
      const firstValidTime = getFirstValidTime(formData.date);
      
      // Check if current time is valid for the selected date
      const isCurrentTimeValid = () => {
        const today = getTodayDate();
        
        if (formData.date === today) {
          const now = new Date();
          const torontoTime = new Date(now.toLocaleString('en-US', { timeZone: 'America/Toronto' }));
          const currentHour24 = torontoTime.getHours();
          const currentMinute = torontoTime.getMinutes();
          
          let selectedHour24 = formData.time.hour;
          if (formData.time.period === "pm" && formData.time.hour !== 12) {
            selectedHour24 = formData.time.hour + 12;
          } else if (formData.time.period === "am" && formData.time.hour === 12) {
            selectedHour24 = 0;
          }
          
          const totalCurrentMinutes = currentHour24 * 60 + currentMinute + 60; // +1 hour buffer
          const totalSelectedMinutes = selectedHour24 * 60 + formData.time.minute;
          
          return totalSelectedMinutes > totalCurrentMinutes;
        }
        
        // For future dates, check if period is valid
        const parseDate = (dateStr: string) => {
          const [year, month, day] = dateStr.split('-').map(Number);
          return new Date(Date.UTC(year, month - 1, day));
        };
        
        const selected = parseDate(formData.date);
        const current = parseDate(getTodayDate());
        const diffDays = Math.round((selected.getTime() - current.getTime()) / (1000 * 60 * 60 * 24));
        
        if (diffDays === 1) {
          // Tomorrow: only PM is valid
          return formData.time.period === "pm";
        }
        
        if (diffDays > 1) {
          // More than 1 day: only PM is valid
          return formData.time.period === "pm";
        }
        
        return true;
      };

      // If current time is not valid, update to first valid time
      if (!isCurrentTimeValid()) {
        setFormData((prev) => ({
          ...prev,
          time: firstValidTime,
        }));
      }
    }

    previousDateRef.current = formData.date;
  }, [formData.date, nowFlag]);

  // Save form data to localStorage whenever formData, selectedPackageId, or nowFlag changes
  // Note: Password is NOT saved to localStorage for security reasons
  useEffect(() => {
    // Skip first save to avoid overwriting with initial empty state
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      return;
    }

    // Create a copy of formData without password for localStorage
    const { password, confirmPassword, ...formDataWithoutPassword } = formData;
    
    const dataToSave = {
      formData: formDataWithoutPassword,
      selectedPackageId,
      nowFlag,
    };
    
    localStorage.setItem('bookingFormData', JSON.stringify(dataToSave));
  }, [formData, selectedPackageId, nowFlag]);

  const formatPhoneNumber = (value: string, prefix: string = ""): string => {
    // Remove todos os caracteres não numéricos
    const numbers = value.replace(/\D/g, '');
    
    // Se o prefixo é +55, aplica formatação brasileira: 11 97665-6681
    if (prefix.startsWith('+55')) {
      // Limita a 11 dígitos (DDD 2 + número 9)
      const limitedNumbers = numbers.slice(0, 11);
      
      // Aplica a formatação brasileira: 11 97665-6681
      if (limitedNumbers.length === 0) {
        return '';
      } else if (limitedNumbers.length <= 2) {
        return limitedNumbers;
      } else if (limitedNumbers.length <= 7) {
        return `${limitedNumbers.slice(0, 2)} ${limitedNumbers.slice(2)}`;
      } else {
        return `${limitedNumbers.slice(0, 2)} ${limitedNumbers.slice(2, 7)}-${limitedNumbers.slice(7)}`;
      }
    }
    
    // Para +1 ou outros, aplica formatação canadense: (123) 456-7890
    // Limita a 10 dígitos
    const limitedNumbers = numbers.slice(0, 10);
    
    // Aplica a formatação canadense: (123) 456-7890
    if (limitedNumbers.length === 0) {
      return '';
    } else if (limitedNumbers.length <= 3) {
      return `(${limitedNumbers}`;
    } else if (limitedNumbers.length <= 6) {
      return `(${limitedNumbers.slice(0, 3)}) ${limitedNumbers.slice(3)}`;
    } else {
      return `(${limitedNumbers.slice(0, 3)}) ${limitedNumbers.slice(3, 6)}-${limitedNumbers.slice(6)}`;
    }
  };

  const formatPostalCode = (value: string): string => {
    // Remove espaços e converte para maiúsculas
    const cleaned = value.replace(/\s/g, '').toUpperCase();
    // Remove caracteres inválidos (mantém apenas letras e números)
    const lettersAndNumbers = cleaned.replace(/[^A-Z0-9]/g, '');
    // Limita a 6 caracteres (formato canadense: A1A1A1)
    const limited = lettersAndNumbers.slice(0, 6);
    
    if (limited.length === 0) {
      return '';
    } else if (limited.length <= 3) {
      return limited;
    } else {
      // Formata como M5A 1N4 (primeiros 3 caracteres + espaço + últimos 3 caracteres)
      return `${limited.slice(0, 3)} ${limited.slice(3)}`;
    }
  };

  const validateField = (field: string, value: string) => {
    let error = "";
    try {
      switch (field) {
        case "fullName":
          NameSchema.parse(value);
          break;
        case "phone":
          // Combine prefix with phone for validation
          // Remove formatting from phone number before combining
          const phoneDigitsOnly = value.replace(/\D/g, '');
          const fullPhone = `${formData.phonePrefix}${phoneDigitsOnly}`;
          PhoneSchema.parse(fullPhone);
          break;
        case "email":
          EmailSchema.parse(value);
          break;
        case "password":
          PasswordSchema.parse(value);
          break;
        case "confirmPassword":
          if (value !== formData.password) {
            error = "Passwords do not match";
          }
          break;
        case "addressLine1":
          AddressSchema.parse(value);
          break;
        case "postalCode":
          if (value.trim()) {
            PostalCodeSchema.parse(value);
          }
          break;
      }
    } catch (err: any) {
      if (err.errors && err.errors[0]) {
        error = err.errors[0].message;
      } else {
        error = err.message || "Invalid value";
      }
    }
    setErrors((prev) => ({ ...prev, [field]: error }));
    return error === "";
  };

  useGooglePlacesAutocomplete(
    addressInputRef,
    (details) => {
    setFormData((prev) => ({
      ...prev,
      addressLine1: details.addressLine1,
      city: details.city,
      postalCode: formatPostalCode(details.postalCode || ''),
      lat: details.lat,
      lng: details.lng,
      serviceZone: details.serviceZone,
    }));
    // Clear validation errors when address is selected from Google Maps
    setErrors((prev) => ({
      ...prev,
      addressLine1: "",
      postalCode: "",
    }));
    toast.success("Address selected from Google Maps");
    },
    {
      cityRef: cityInputRef,
      postalCodeRef: postalCodeInputRef,
    }
  );

  const handleLocateMe = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by your browser");
      return;
    }

    toast.loading("Getting your location...");

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;

        try {
          // Use Google Maps Geocoding API to reverse geocode
          const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
          const response = await fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${apiKey}`
          );
          const data = await response.json();

          if (data.results && data.results.length > 0) {
            const result = data.results[0];
            let addressLine1 = "";
            let city = "";
            let postalCode = "";

            result.address_components.forEach((component: any) => {
              const types = component.types;

              if (types.includes("street_number")) {
                addressLine1 = component.long_name + " ";
              }
              if (types.includes("route")) {
                addressLine1 += component.long_name;
              }
              if (types.includes("locality")) {
                city = component.long_name;
              }
              if (types.includes("postal_code")) {
                postalCode = component.long_name;
              }
            });

            const serviceZone = getServiceZone(latitude, longitude);

            setFormData((prev) => ({
              ...prev,
              addressLine1: addressLine1.trim(),
              city: city || prev.city,
              postalCode: formatPostalCode(postalCode || ''),
              lat: latitude,
              lng: longitude,
              serviceZone,
            }));

            // Clear validation errors when location is detected
            setErrors((prev) => ({
              ...prev,
              addressLine1: "",
              postalCode: "",
            }));

            toast.dismiss();
            toast.success("Location detected successfully!");
          } else {
            toast.dismiss();
            toast.error("Could not find address for your location");
          }
        } catch (error) {
          console.error("Geocoding error:", error);
          toast.dismiss();
          toast.error("Failed to get address from location");
        }
      },
      (error) => {
        toast.dismiss();
        console.error("Geolocation error:", error);
        
        switch (error.code) {
          case error.PERMISSION_DENIED:
            toast.error("Location permission denied. Please enable location access in your browser settings.");
            break;
          case error.POSITION_UNAVAILABLE:
            toast.error("Location information is unavailable.");
            break;
          case error.TIMEOUT:
            toast.error("Location request timed out.");
            break;
          default:
            toast.error("An unknown error occurred while getting your location.");
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  };

  useEffect(() => {
    // Fetch packages from Supabase
    fetchPackages();
  }, [location]);

  // Reload selected package from localStorage when component mounts (when returning from select-package page)
  // React Router will remount the component when navigating, so this will run automatically
  useEffect(() => {
    try {
      const savedData = localStorage.getItem('bookingFormData');
      if (savedData) {
        const parsed = JSON.parse(savedData);
        if (parsed.selectedPackageId) {
          setSelectedPackageId(parsed.selectedPackageId);
        }
      }
    } catch (error) {
      console.error('Error loading selected package:', error);
    }
  }, []);


  const fetchPackages = async () => {
    try {
      const DEFAULT_LOCATION = 'toronto';
      
      // First get the location ID
      const { data: locationData, error: locationError } = await supabase
        .from("locations")
        .select("id")
        .eq("code", location)
        .eq("is_active", true)
        .maybeSingle();

      if (locationError) throw locationError;
      
      // Se a location não for encontrada, tenta fallback para default
      let finalLocationData = locationData;
      let finalLocationCode = location;
      
      if (!locationData && location !== DEFAULT_LOCATION) {
        console.warn(`Location '${location}' not found, falling back to '${DEFAULT_LOCATION}'`);
        
        const { data: fallbackData, error: fallbackError } = await supabase
          .from("locations")
          .select("id")
          .eq("code", DEFAULT_LOCATION)
          .eq("is_active", true)
          .maybeSingle();
        
        if (fallbackError) {
          console.error("Error fetching fallback location:", fallbackError);
          throw fallbackError;
        }
        
        if (fallbackData) {
          finalLocationData = fallbackData;
          finalLocationCode = DEFAULT_LOCATION;
        }
      }
      
      if (!finalLocationData) {
        toast.error("Location not found");
        return;
      }

      // Then fetch packages for this location
      const { data, error } = await supabase
        .from("packages")
        .select("*")
        .eq("location_id", finalLocationData.id)
        .eq("is_active", true)
        .order("base_price_cents", { ascending: true });

      if (error) throw error;
      
      if (finalLocationCode !== location) {
        console.log(`Using fallback location '${finalLocationCode}' instead of '${location}'`);
      }
      
      setPackages(data || []);
    } catch (error) {
      console.error("Error fetching packages:", error);
      toast.error("Failed to load packages");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedPackageId) {
      toast.error("Please select a package");
      return;
    }

    // Rate limiting check
    if (!checkBookingRateLimit()) {
      toast.error("Too many booking attempts. Please wait before trying again.");
      return;
    }

    // Validate and sanitize all inputs using Zod
    try {
      // Prepare data for validation - combine phone prefix with phone number
      // Remove formatting from phone number before combining
      const phoneDigits = formData.phone.replace(/\D/g, '');
      const phoneWithPrefix = `${formData.phonePrefix}${phoneDigits}`;
      
      const validationData = {
        fullName: formData.fullName,
        phonePrefix: formData.phonePrefix,
        phoneCountryCode: formData.phoneCountryCode,
        phone: phoneWithPrefix,
        email: formData.email,
        password: formData.password,
        confirmPassword: formData.confirmPassword,
        addressLine1: formData.addressLine1,
        city: formData.city,
        postalCode: formData.postalCode,
        addressLine2: formData.addressLine2 || "",
        date: formData.date,
        time: formData.time,
        notes: formData.notes || "",
        lat: formData.lat,
        lng: formData.lng,
        serviceZone: formData.serviceZone,
      };

      // Validate with Zod (this will sanitize and transform data)
      const validated = BookingInputSchema.parse(validationData);

      // Update formData with sanitized values
      const sanitizedFormData = {
        ...formData,
        fullName: validated.fullName,
        email: validated.email,
        addressLine1: validated.addressLine1,
        postalCode: validated.postalCode,
        notes: validated.notes,
      };

      // Store all data in localStorage (for persistence when user comes back)
      const dataToSave = {
        formData: sanitizedFormData,
        selectedPackageId,
        nowFlag,
      };
      localStorage.setItem("bookingFormData", JSON.stringify(dataToSave));

      // Also store in sessionStorage (for verification step)
      // Convert 12-hour time to 24-hour format and create proper datetime in Toronto timezone
      let hour24 = formData.time.hour;
      if (formData.time.period === "pm" && hour24 !== 12) {
        hour24 = hour24 + 12;
      } else if (formData.time.period === "am" && hour24 === 12) {
        hour24 = 0;
      }
      
      // Create proper ISO string for Toronto timezone conversion
      const scheduledAt = nowFlag 
        ? new Date().toISOString()
        : (() => {
            const [year, month, day] = formData.date.split('-').map(Number);
            
            // Find the UTC time that converts to our desired Toronto time
            // Toronto is EST (UTC-5) or EDT (UTC-4), so UTC = Toronto + offset
            for (let testOffset = -12; testOffset <= 12; testOffset++) {
              // Create UTC time candidate (Toronto + offset hours)
              const utcCandidate = new Date(Date.UTC(year, month - 1, day, hour24 + testOffset, formData.time.minute, 0));
              
              // Check what Toronto time this UTC represents
              const torontoStr = utcCandidate.toLocaleString('en-US', {
                timeZone: 'America/Toronto',
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
              });
              
              // Parse Toronto time
              const parts = torontoStr.replace(',', '').split(/[\/:\s]/);
              const torontoHour = parseInt(parts[3]);
              const torontoMin = parseInt(parts[4]);
              
              // If matches our target, we found it!
              if (torontoHour === hour24 && torontoMin === formData.time.minute) {
                return utcCandidate.toISOString();
              }
            }
            
            // Fallback (should not reach here)
            return new Date(Date.UTC(year, month - 1, day, hour24, formData.time.minute, 0)).toISOString();
          })();
      
      sessionStorage.setItem(
        "bookingFormData",
        JSON.stringify({
          ...sanitizedFormData,
          selectedPackageId,
          nowFlag,
          scheduledAt,
        }),
      );

      navigate("/book/verify");
    } catch (error: any) {
      // Handle validation errors - extract user-friendly message
      const errorMessage = sanitizeError(error);
      toast.error(errorMessage);
      
      // Set field-specific errors if available (for inline field validation)
      if (error && typeof error === "object" && "errors" in error && Array.isArray(error.errors)) {
        const fieldErrors: Record<string, string> = {};
        error.errors.forEach((err: any) => {
          const field = err.path?.[0];
          if (field && err.message) {
            fieldErrors[field] = err.message;
          }
        });
        if (Object.keys(fieldErrors).length > 0) {
          setErrors((prev) => ({ ...prev, ...fieldErrors }));
        }
      }
    }
  };

  const phoneDigits = (formData.phone || "").replace(/\D/g, '').length;
  const requiredPhoneDigits = (formData.phonePrefix || "+1").startsWith('+55') ? 11 : 10;
  
  // Check if selected date is weekend
  const isWeekendSelected = 
    (nowFlag && isWeekendDate(getTodayDate())) || 
    (formData.date && !nowFlag && isWeekendDate(formData.date));
  
  const isFormValid =
    selectedPackageId &&
    formData.fullName?.trim().length >= 5 &&
    phoneDigits === requiredPhoneDigits &&
    formData.email?.trim().length > 0 &&
    formData.password?.length >= 8 &&
    formData.password === formData.confirmPassword &&
    formData.addressLine1?.trim().length >= 5 &&
    formData.city?.trim() &&
    formData.postalCode?.replace(/\s/g, '').length === 6 &&
    (nowFlag || formData.date) &&
    !errors.fullName &&
    !errors.phone &&
    !errors.email &&
    !errors.password &&
    !errors.confirmPassword &&
    !errors.addressLine1 &&
    !errors.postalCode &&
    formData.serviceZone !== "OUT_OF_COVERAGE" &&
    !isWeekendSelected; // Block if weekend is selected

  const formatPrice = (cents: number) => {
    return `$${(cents / 100).toLocaleString("en-CA", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    
    if (hours === 0) {
      return `${remainingMinutes}-min shoot`;
    } else if (remainingMinutes === 0) {
      return `${hours}-hour${hours > 1 ? 's' : ''} shoot`;
    } else {
      return `${hours}h${remainingMinutes}min shoot`;
    }
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB] pb-32">
      <PageHeader variant="back" title="Book Service" backTo="/" />

      <form onSubmit={handleSubmit} className="px-4 py-6 space-y-8">
        {/* Progress */}
        <StepProgress currentStep={1} stepLabel="Package Selection" />

        {/* Coverage Banner */}
        <Card className="bg-primary/5 border-primary/20 p-4 flex gap-3">
          <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
            <MapPin className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-foreground mb-1">
              Downtown {location.charAt(0).toUpperCase() + location.slice(1)} Coverage
            </h3>
            <p className="text-sm text-foreground/80">
              We service the entire downtown zone with guaranteed arrival within 99 minutes of your booking
              confirmation.
            </p>
          </div>
        </Card>

        {/* Package Selection */}
        <div>
          <div className="flex justify-between items-baseline mb-4">
            <h2 className="text-lg font-semibold">Select Your Package</h2>
            {packages.length > 3 && (
              <Button 
                type="button"
                variant="outline"
                onClick={() => navigate("/book/select-package")}
                className="border-primary text-primary bg-[#F9FAFB] hover:bg-primary hover:text-primary-foreground font-semibold gap-2"
                size="sm"
              >
                Explore All {packages.length} Packages
                <ChevronRight className="h-4 w-4" />
              </Button>
            )}
          </div>

          <div className="space-y-3">
            {loading ? (
              <>
                {[1, 2, 3].map((i) => (
                  <Card key={i} className="p-4 bg-[#F4F5F7] relative overflow-hidden">
                    <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-white/40 to-transparent" />
                    <div className="flex justify-between items-start mb-2">
                      <div className="h-6 w-32 bg-muted/60 rounded animate-pulse"></div>
                      <div className="h-6 w-16 bg-muted/60 rounded animate-pulse"></div>
                    </div>
                    <div className="h-4 w-full bg-muted/60 rounded mb-2 animate-pulse"></div>
                    <div className="h-4 w-3/4 bg-muted/60 rounded mb-3 animate-pulse"></div>
                    <div className="h-4 w-24 bg-muted/60 rounded animate-pulse"></div>
                  </Card>
                ))}
              </>
            ) : packages.length === 0 ? (
              <Card className="p-4 bg-[#F4F5F7]">
                <p className="text-muted-foreground text-center">No packages available</p>
              </Card>
            ) : (
              packages.slice(0, 3).map((pkg, index) => {
                const isSelected = selectedPackageId === pkg.id;
                return (
                  <Card
                    key={pkg.id}
                    className={`p-4 cursor-pointer transition-all animate-fade-in ${
                      isSelected
                        ? "border-primary border-2 shadow-sm bg-primary/5"
                        : "bg-[#F4F5F7] border-border hover:border-primary/50"
                    }`}
                    style={{ animationDelay: `${index * 100}ms` }}
                    onClick={() => setSelectedPackageId(pkg.id)}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center gap-2">
                        {isSelected && (
                          <div className="h-2 w-2 rounded-full bg-primary animate-pulse"></div>
                        )}
                        <h3 className={`font-semibold ${isSelected ? "text-primary" : "text-foreground"}`}>
                          {pkg.name}
                        </h3>
                      </div>
                      <span className={`font-bold text-lg ${isSelected ? "text-primary" : ""}`}>
                        {formatPrice(pkg.base_price_cents)}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground mb-3">{pkg.description}</p>
                    <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                      <Clock className="h-4 w-4" />
                      <span>{formatDuration(pkg.duration_minutes)}</span>
                    </div>
                    <div className="mt-3 flex justify-end">
                      <div
                        className={`h-6 w-6 rounded-full border-2 flex items-center justify-center transition-all ${
                          isSelected ? "border-primary bg-primary scale-110" : "border-input"
                        }`}
                      >
                        {isSelected && <div className="h-3 w-3 rounded-full bg-primary-foreground"></div>}
                      </div>
                    </div>
                  </Card>
                );
              })
            )}
          </div>

          {/* Selected Package Indicator (if selected package is not in the first 3) */}
          {selectedPackageId && !loading && packages.length > 0 && (() => {
            const selectedPackage = packages.find(p => p.id === selectedPackageId);
            const isInFirstThree = packages.slice(0, 3).some(p => p.id === selectedPackageId);
            
            if (selectedPackage && !isInFirstThree) {
              return (
                <Card className="mt-3 p-4 border-2 border-primary/30 bg-gradient-to-br from-primary/5 to-primary/10 shadow-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="h-2 w-2 rounded-full bg-primary animate-pulse shrink-0"></div>
                        <span className="text-xs font-semibold text-primary tracking-wide">
                          Selected Package
                        </span>
                      </div>
                      <h3 className="font-semibold text-foreground text-base mb-1 truncate">
                        {selectedPackage.name}
                      </h3>
                      <div className="flex items-baseline gap-2">
                        <span className="font-bold text-lg text-primary">
                          {formatPrice(selectedPackage.base_price_cents)}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          • {formatDuration(selectedPackage.duration_minutes)}
                        </span>
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => navigate("/book/select-package")}
                      className="shrink-0 text-xs h-8 px-3 border-primary/30 hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors"
                    >
                      Change
                    </Button>
                  </div>
                </Card>
              );
            }
            return null;
          })()}
        </div>

        {/* About You */}
        <section className="space-y-4">
          <h2 className="text-lg font-semibold">About You</h2>

          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              placeholder="Enter Your Name"
              value={formData.fullName}
              onChange={(e) => {
                // Preserve spaces during typing
                const value = sanitizeString(e.target.value.slice(0, 100), true);
                setFormData({ ...formData, fullName: value });
                if (errors.fullName) validateField("fullName", value);
              }}
              onBlur={(e) => {
                // Apply full sanitization on blur (normalize spaces)
                const sanitized = sanitizeString(e.target.value, false);
                setFormData({ ...formData, fullName: sanitized });
                validateField("fullName", sanitized);
              }}
              required
              className={errors.fullName ? "border-destructive" : ""}
            />
            {errors.fullName && (
              <p className="text-sm text-destructive">{errors.fullName}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">Phone Number</Label>
            <div className="flex gap-2">
              <CountryPhoneSelector
                value={formData.phonePrefix}
                countryCode={formData.phoneCountryCode}
                onChange={(newPrefix, newCountryCode) => {
                  // Reformata o telefone quando o prefixo muda
                  const currentPhoneDigits = formData.phone.replace(/\D/g, '');
                  const reformatted = formatPhoneNumber(currentPhoneDigits, newPrefix);
                  setFormData({ 
                    ...formData, 
                    phonePrefix: newPrefix, 
                    phoneCountryCode: newCountryCode, // Memoriza o código do país selecionado (ex: "US" ou "CA")
                    phone: reformatted 
                  });
                }}
              />
              <Input
                id="phone"
                type="tel"
                placeholder={formData.phonePrefix.startsWith('+55') ? "11 97665-6681" : "(123) 456-7890"}
                value={formData.phone}
                onChange={(e) => {
                  const formatted = formatPhoneNumber(e.target.value, formData.phonePrefix);
                  const sanitized = sanitizePhone(formatted, formData.phonePrefix);
                  setFormData({ ...formData, phone: formatted });
                  if (errors.phone) validateField("phone", formatted);
                }}
                onBlur={(e) => validateField("phone", e.target.value)}
                required
                className={`flex-1 ${errors.phone ? "border-destructive" : ""}`}
              />
            </div>
            {errors.phone && (
              <p className="text-sm text-destructive">{errors.phone}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="Enter Your Email"
              value={formData.email || ""}
              onChange={(e) => {
                const value = sanitizeEmail(e.target.value.slice(0, 255));
                setFormData({ ...formData, email: value });
                if (errors.email) validateField("email", value);
              }}
              onBlur={(e) => validateField("email", e.target.value)}
              required
              className={errors.email ? "border-destructive" : ""}
            />
            {errors.email && (
              <p className="text-sm text-destructive">{errors.email}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              placeholder="Enter Your Password"
              value={formData.password || ""}
              onChange={(e) => {
                const value = e.target.value;
                setFormData({ ...formData, password: value });
                if (errors.password) validateField("password", value);
                // Revalidate confirmPassword when password changes
                if (formData.confirmPassword) {
                  validateField("confirmPassword", formData.confirmPassword);
                }
              }}
              onBlur={(e) => validateField("password", e.target.value)}
              required
              className={errors.password ? "border-destructive" : ""}
            />
            {errors.password && (
              <p className="text-sm text-destructive">{errors.password}</p>
            )}
            <p className="text-xs text-muted-foreground">
              Must be at least 8 characters with uppercase, lowercase, and number
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Confirm Password</Label>
            <Input
              id="confirmPassword"
              type="password"
              placeholder="Confirm Your Password"
              value={formData.confirmPassword || ""}
              onChange={(e) => {
                const value = e.target.value;
                setFormData({ ...formData, confirmPassword: value });
                if (errors.confirmPassword) validateField("confirmPassword", value);
              }}
              onBlur={(e) => validateField("confirmPassword", e.target.value)}
              required
              className={errors.confirmPassword ? "border-destructive" : ""}
            />
            {errors.confirmPassword && (
              <p className="text-sm text-destructive">{errors.confirmPassword}</p>
            )}
          </div>
        </section>

        {/* Service Address */}
        <section className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold">Service Address</h2>
            <button 
              type="button" 
              onClick={handleLocateMe}
              className="inline-flex items-center gap-2 rounded-md border border-primary/40 bg-primary/10 px-3 py-2 text-sm font-semibold text-primary transition hover:bg-primary/20 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
            >
              <MapPin className="h-5 w-5" />
              Locate Me
            </button>
          </div>

          <div className="space-y-2">
            <Label htmlFor="address">Address</Label>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                ref={addressInputRef}
                id="address"
                placeholder="Start typing your address..."
                value={formData.addressLine1}
                onChange={(e) => {
                  // Preserve spaces during typing
                  const value = sanitizeAddress(e.target.value.slice(0, 200), true);
                  setFormData({ ...formData, addressLine1: value });
                  if (errors.addressLine1) validateField("addressLine1", value);
                }}
                onBlur={(e) => {
                  // Apply full sanitization on blur (normalize spaces)
                  const sanitized = sanitizeAddress(e.target.value, false);
                  setFormData({ ...formData, addressLine1: sanitized });
                  validateField("addressLine1", sanitized);
                }}
                required
                className={`pl-10 ${errors.addressLine1 ? "border-destructive" : ""}`}
              />
            </div>
            {errors.addressLine1 && (
              <p className="text-sm text-destructive">{errors.addressLine1}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="city">City</Label>
              <Input
                ref={cityInputRef}
                id="city"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value.slice(0, 100) })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="postal">Postal Code</Label>
              <Input
                ref={postalCodeInputRef}
                id="postal"
                placeholder="M5A 1N4"
                value={formData.postalCode}
                onChange={(e) => {
                  const formatted = formatPostalCode(e.target.value);
                  setFormData({ ...formData, postalCode: formatted });
                  if (errors.postalCode) validateField("postalCode", formatted);
                }}
                onBlur={async (e) => {
                  validateField("postalCode", e.target.value);
                  // Geocode postal code to get lat/lng and service zone
                  const postalCode = e.target.value.replace(/\s/g, '');
                  if (postalCode.length === 6) {
                    try {
                      const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
                      const response = await fetch(
                        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(postalCode + ', Canada')}&key=${apiKey}`
                      );
                      const data = await response.json();
                      
                      if (data.results && data.results.length > 0) {
                        const result = data.results[0];
                        const latitude = result.geometry.location.lat;
                        const longitude = result.geometry.location.lng;
                        const serviceZone = getServiceZone(latitude, longitude);
                        
                        setFormData((prev) => ({
                          ...prev,
                          lat: latitude,
                          lng: longitude,
                          serviceZone,
                        }));
                      }
                    } catch (error) {
                      console.error('Geocoding error:', error);
                    }
                  }
                }}
                required
                className={errors.postalCode ? "border-destructive" : ""}
                maxLength={7}
              />
              {errors.postalCode && (
                <p className="text-sm text-destructive">{errors.postalCode}</p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="unit">Apartment / Unit / Suite</Label>
            <Input
              id="unit"
              placeholder="Optional"
              value={formData.addressLine2}
              onChange={(e) => setFormData({ ...formData, addressLine2: e.target.value.slice(0, 100) })}
            />
          </div>

          {/* Service Zone Indicator */}
          {formData.serviceZone && (
            <div className="mt-3">
              {formData.serviceZone === "FRZ" && (
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                  <Badge variant="default" className="bg-green-500 hover:bg-green-600 w-fit shrink-0">
                    Express Zone (FRZ)
                  </Badge>
                  <span className="text-sm text-muted-foreground sm:flex-1">
                    Your address is in our premium coverage area
                  </span>
                </div>
              )}
              {formData.serviceZone === "SRZ" && (
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                  <Badge variant="secondary" className="bg-blue-500 hover:bg-blue-600 w-fit shrink-0">
                    Standard Zone (SRZ)
                  </Badge>
                  <span className="text-sm text-muted-foreground sm:flex-1">
                    Your address is in our standard coverage area
                  </span>
                </div>
              )}
              {formData.serviceZone === "OUT_OF_COVERAGE" && (
                <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3">
                  <Badge variant="destructive" className="bg-red-500 hover:bg-red-600 w-fit shrink-0">
                    <AlertTriangle className="h-3 w-3 mr-1" />
                    Out of Coverage
                  </Badge>
                  <span className="text-sm text-muted-foreground sm:flex-1">
                    Unfortunately, we don't currently service this area. Please contact us for alternative arrangements.
                  </span>
                </div>
              )}
            </div>
          )}
        </section>

        {/* Schedule */}
        <section className="space-y-4">
          <h2 className="text-lg font-semibold">Schedule Date and Time</h2>

          <div className="space-y-2">
            <Label htmlFor="date" className={nowFlag ? "text-muted-foreground" : ""}>
              Date
            </Label>
            <div className="relative">
              <CalendarIcon className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 transition-colors z-10 pointer-events-none ${
                nowFlag ? "text-muted-foreground/50" : "text-muted-foreground"
              }`} />
              <Input
                id="date"
                type="date"
                value={formData.date}
                min={getTodayDate()}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                required={!nowFlag}
                disabled={nowFlag}
                className={`pl-10 pr-12 transition-all ${
                  nowFlag 
                    ? "bg-muted/50 text-muted-foreground cursor-not-allowed opacity-60" 
                    : ""
                } [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-3 [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-100 [&::-webkit-calendar-picker-indicator]:w-5 [&::-webkit-calendar-picker-indicator]:h-5 [&::-webkit-calendar-picker-indicator]:z-0`}
              />
            </div>
            {nowFlag && (
              <p className="text-xs text-muted-foreground">
                Date is automatically set to today when "Now" is enabled
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label>Time</Label>
            <TimeSelector
              value={formData.time}
              onChange={(time) => setFormData({ ...formData, time })}
              selectedDate={formData.date}
              currentDate={getTodayDate()}
            />
          </div>

          <div className="flex items-center justify-between py-4">
            <Label htmlFor="now" className="text-lg font-semibold">
              Now
            </Label>
            <Switch
              id="now"
              checked={nowFlag}
              onCheckedChange={setNowFlag}
              className="h-8 w-14 shrink-0 border-2 border-primary/60 data-[state=checked]:bg-primary data-[state=unchecked]:bg-[hsl(0deg_0%_75.74%_/_60%)]"
              thumbClassName="h-7 w-7 data-[state=checked]:!translate-x-7"
            />
          </div>

          {nowFlag && (
            <Card className={isWeekendDate(getTodayDate()) ? "bg-destructive/10 border-destructive/20 p-4" : "bg-primary/5 border-primary/20 p-4"}>
              <p className="text-sm text-foreground/80">
                {isWeekendDate(getTodayDate()) 
                  ? "Service is not available on weekends. Please select a weekday."
                  : `You'll have our team with you in the next 99 minutes when inside the ${location.charAt(0).toUpperCase() + location.slice(1)} Downtown Area. A same-day fee will be charged.`}
              </p>
            </Card>
          )}
        </section>

        {/* Notes */}
        <section className="space-y-4">
          <h2 className="text-lg font-semibold">Notes & Additional Information</h2>
          <Textarea
            placeholder="Any additional details or special requests..."
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value.slice(0, 1000) })}
            rows={6}
          />
        </section>
      </form>

      {/* Fixed Bottom CTA */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-border p-4">
        <Button
          type="submit"
          className="w-full h-14 text-base font-semibold rounded-xl"
          onClick={handleSubmit}
          disabled={!isFormValid}
        >
          Continue to Verification
        </Button>
      </div>
    </div>
  );
}
